import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteOrdersView,
    getRouteReportsSavingsSummary,
} from 'shared/const/router';
import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { BackButton } from 'shared/ui/BackButton';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { DynamicTooltip } from 'shared/ui/Tooltip';
import {
    ComposableReportWidget,
    type ReportWidgetConfig,
    ReportWidgetInteractionPayload,
    type ReportWidgetItem,
} from 'widgets/ComposableReportWidget';
import { Page } from 'widgets/Page';

import { ProductDrillInContextHeader } from './ProductDrillInContextHeader';

import { useReportsPageSavingsSummaryProduct } from '../model/hooks/useReportsPageSavingsSummaryProduct';

const ReportsPageSavingsSummaryProduct = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        sku,
        product: productFromState,
        fromMissedSavings,
        loading,
        totalSaved,
        totalSpent,
        totalQuantity,
        ordersCount,
        ordersData,
        savingsPercentLabel,
        lastRequestParamsPresent,
        canLoadMoreOrders,
        loadMoreOrders,
    } = useReportsPageSavingsSummaryProduct();

    const summaryAmount = fromMissedSavings
        ? Number(productFromState?.missedSavings ?? 0)
        : totalSaved;
    const bestPriceAmount = Math.max(0, totalSpent - summaryAmount);

    const summaryPercentLabel = fromMissedSavings
        ? formatReportPercent(productFromState?.missedSavingsPercent ?? '0')
        : savingsPercentLabel;

    const primaryMetricColor = fromMissedSavings
        ? 'metrics.warning'
        : 'metrics.positive';

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const openOrderDetailsInNewTab = useCallback((orderId: string) => {
        window.open(
            getRouteOrdersView(orderId),
            '_blank',
            'noopener,noreferrer',
        );
    }, []);

    const handleOrderDetailsClick = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const orderId = payload.item.meta?.orderId;
            if (typeof orderId === 'string') {
                openOrderDetailsInNewTab(orderId);
            }
        },
        [openOrderDetailsInNewTab],
    );

    if (!sku || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-product"
            padding="24px 35px"
        >
            <Box>
                <BackButton
                    onBack={handleBack}
                    title={t('Savings Summary')}
                    id="back-to-savings-summary-button"
                />

                <ProductDrillInContextHeader
                    sku={sku}
                    product={productFromState}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <ReportMetric
                        title={t(
                            fromMissedSavings
                                ? 'savingsSummary.missedSavings'
                                : 'savingsSummary.productsMetricTotalSavings',
                            fromMissedSavings
                                ? 'Missed Savings'
                                : 'Total Savings',
                        )}
                        primary={formatReportCurrency(summaryAmount)}
                        primaryColor={primaryMetricColor}
                        primaryTooltip={t(
                            fromMissedSavings
                                ? 'savingsSummary.missedSavingsTooltip'
                                : undefined,
                        )}
                        loading={loading}
                        minHeight="172px"
                    >
                        <Box
                            sx={{
                                fontSize: '12px',
                                lineHeight: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                <span>
                                    {t(
                                        'savingsSummary.competitiveSpend',
                                        'Competitive Spend',
                                    )}
                                </span>
                                <DynamicTooltip
                                    content={t(
                                        'savingsSummary.competitiveSpendTooltip',
                                    )}
                                    containerStyle={{
                                        height: '16px',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: '#B7B7B7',
                                        }}
                                        data-pdf-tooltip="true"
                                    />
                                </DynamicTooltip>
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                }}
                            >
                                {formatReportCurrency(totalSpent)}
                            </Box>
                        </Box>
                        {fromMissedSavings && (
                            <Box
                                sx={{
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: 0.5,
                                }}
                            >
                                <span>
                                    {t(
                                        'savingsSummary.missedSavingsColumnBestPrice',
                                        'Best Price',
                                    )}
                                </span>
                                <Box
                                    component="span"
                                    sx={{
                                        fontVariantNumeric: 'tabular-nums',
                                        fontWeight: 600,
                                    }}
                                >
                                    {formatReportCurrency(bestPriceAmount)}
                                </Box>
                            </Box>
                        )}
                    </ReportMetric>
                    <ReportMetric
                        title={t(
                            fromMissedSavings
                                ? 'savingsSummary.percentOfMissedSavings'
                                : 'savingsSummary.percentOfTotalSavings',
                            fromMissedSavings
                                ? '% of Missed Savings'
                                : '% of Total Savings',
                        )}
                        primary={summaryPercentLabel || '-'}
                        primaryColor={primaryMetricColor}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricTotalQuantity',
                            'Total Quantity',
                        )}
                        primary={totalQuantity.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.productsMetricOrders',
                            'Orders',
                        )}
                        primary={ordersCount.toLocaleString()}
                        loading={loading}
                        minHeight="172px"
                    />
                </Box>

                <Box sx={{ marginTop: 4 }}>
                    <ComposableReportWidget
                        config={
                            {
                                widgetId: 'savings-summary-product-orders',
                                title: t(
                                    'savingsSummary.productsPurchasesTitle',
                                    'Purchase History',
                                ),
                                layout: 'stacked',
                                onDetailsClick: handleOrderDetailsClick,
                                visualization: {
                                    enabled: false,
                                    type: 'bar',
                                },
                                pagination: {
                                    mode: 'server',
                                    pageSize: 25,
                                    canLoadMore: canLoadMoreOrders,
                                    onLoadMore: loadMoreOrders,
                                },
                                table: {
                                    enabled: true,
                                    columns: [
                                        {
                                            id: 'date',
                                            label: t('Date'),
                                            valueKey: 'date',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'order',
                                            label: t('Order'),
                                            valueKey: 'orderNumber',
                                            format: 'text',
                                            align: 'left',
                                            bold: true,
                                            isDetailsTrigger: true,
                                        },
                                        {
                                            id: 'property',
                                            label: t('Property'),
                                            valueKey: 'propertyName',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'supplier',
                                            label: t('Supplier'),
                                            valueKey: 'suppliers',
                                            format: 'text',
                                            align: 'left',
                                        },
                                        {
                                            id: 'quantity',
                                            label: t('Quantity'),
                                            valueKey: 'quantity',
                                            format: 'number',
                                            align: 'right',
                                        },
                                        {
                                            id: 'avgSupplierPrice',
                                            label: fromMissedSavings
                                                ? t(
                                                      'savingsSummary.missedSavingsColumnBestPrice',
                                                      'Best Price',
                                                  )
                                                : t('Avg Supplier Price'),
                                            valueKey: 'avgSupplierPrice',
                                            format: 'currency',
                                            align: 'right',
                                        },
                                        {
                                            id: 'pricePaid',
                                            label: t('Price Paid'),
                                            valueKey: 'pricePaid',
                                            format: 'currency',
                                            align: 'right',
                                        },
                                        {
                                            id: 'savings',
                                            label: fromMissedSavings
                                                ? t(
                                                      'savingsSummary.missedSavings',
                                                      'Missed Savings',
                                                  )
                                                : t('Savings'),
                                            valueKey: 'savings',
                                            format: 'currency',
                                            align: 'right',
                                            highlightColor: fromMissedSavings
                                                ? 'metrics.warning'
                                                : 'metrics.positive',
                                        },
                                        ...(fromMissedSavings
                                            ? []
                                            : [
                                                  {
                                                      id: 'percentSaved',
                                                      label: t(
                                                          'savingsSummary.productsColumnPercentSaved',
                                                      ),
                                                      valueKey: 'percentSaved',
                                                      format: 'percent',
                                                      align: 'right',
                                                      highlightColor:
                                                          'metrics.positive',
                                                  },
                                              ]),
                                    ],
                                    options: {
                                        emptyMessage: t(
                                            'savingsSummary.productsPurchasesEmpty',
                                            'No purchases found for this product',
                                        ),
                                    },
                                },
                            } as ReportWidgetConfig
                        }
                        items={(ordersData?.items ?? [])
                            .filter((orderItem) =>
                                fromMissedSavings
                                    ? (orderItem.totalMissed ?? 0) > 0
                                    : orderItem.totalSaved > 0,
                            )
                            .map((orderItem, index): ReportWidgetItem => {
                                const submittedDate = new Date(
                                    orderItem.submittedAt,
                                );
                                const dateLabel = new Intl.DateTimeFormat(
                                    undefined,
                                    {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    },
                                ).format(submittedDate);

                                let avgSupplierTotal = 0;
                                let savingsValue = 0;

                                if (fromMissedSavings) {
                                    savingsValue = orderItem.totalMissed ?? 0;
                                    avgSupplierTotal =
                                        orderItem.totalPaid - savingsValue;
                                } else {
                                    savingsValue = orderItem.totalSaved;
                                    avgSupplierTotal =
                                        orderItem.baselinePrice *
                                        orderItem.quantity;
                                }

                                return {
                                    id: `${orderItem.order.orderId}-${index}`,
                                    label: orderItem.order.orderNumber,
                                    primaryValue: savingsValue,
                                    secondaryValues: {
                                        date: dateLabel,
                                        orderNumber:
                                            orderItem.order.orderNumber,
                                        propertyName: orderItem.propertyName,
                                        suppliers:
                                            orderItem.suppliers.join(', '),
                                        quantity: orderItem.quantity,
                                        pricePaid: orderItem.totalPaid,
                                        avgSupplierPrice: avgSupplierTotal,
                                        savings: savingsValue,
                                        percentSaved: orderItem.percentSaved,
                                    },
                                    meta: { orderId: orderItem.order.orderId },
                                };
                            })}
                        loading={loading}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryProduct;
